import { logError } from 'App/services/faroService';
import { analyticsTrackDynamicScreenerStart } from 'Shared/services/analytics/events/dynamicScreenerStart';
import { getDynamicScreenerTestData } from 'Shared/services/opa/app/dynamicScreenerSplitTest';
import { getUserCacheData, setUserCacheData, UserSessionCacheKeys } from 'Shared/services/sessionCacheService';
import { getDynamicScreeningQuestionsForProviders } from 'Shared/services/usp/api/uspApiService';
import { UspSurveysModel } from 'Shared/services/usp/models/UspSurveysModel';
import { USPAnswerType,UspUnansweredQuestions } from 'Shared/services/usp/models/UspUnansweredQuestionsModel';

import { buildDynamicScreenerKey } from './questionService';

export interface DynamicScreenerQuestion {
  id         : string;
  providerId : string;
  question   : string;
  displayMode: {
    type: 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'OPEN_ENDED';
    inputType?: 'TEXT' | 'NUMBER' | 'UNKNOWN' | 'UNDEFINED';
    constraints?: {
      constraintType?: 'NUMBER' | 'TEXT';
      min?: number;
      max?: number;
      jsRegex?: string;
    };
  };
  answers: Array<{
    id   : string;
    label: string;
  }>;
}

export interface DynamicScreenerCache {
  updatedAt: number;
  questions: {
    [key: string]: DynamicScreenerQuestion[];
  };
}

/**
 * Get the dynamic screener cache
 * @returns the dynamic screener cache
 */
const getDynamicScreenerCache = (): DynamicScreenerCache => {
  return (
    getUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS) ?? {
      updatedAt: Date.now(),
      questions: {},
    }
  ) as DynamicScreenerCache;
};

/**
 * Save the dynamic screener cache to local storage
 * @param cache the dynamic screener cache
 */
const setDynamicScreenerCache = (cache: DynamicScreenerCache): void => {
  cache.updatedAt = Date.now();
  setUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS, cache);
};

/**
 * In-flight dedup: identical concurrent fetches (same item set, order-independent)
 * share a single POST. The pending promise resolves to the questions array and is
 * cleared from the map once settled so sequential (awaited) calls fetch normally.
 */
const inFlightQuestionFetches = new Map<string, Promise<DynamicScreenerQuestion[]>>();

/**
 * Build a stable, order-independent key for an item set so concurrent calls with
 * the same questions (in any order) hash to the same in-flight entry.
 */
const buildInFlightKey = (
  items: Array<{ questionId: string; providerId: string }>
): string => items.map((i) => `${i.providerId}:${i.questionId}`).sort().join('|');

/**
 * Fetches screening questions for all providers in a single POST request
 * (multi-provider model). Groups the returned questions into the cache keyed by
 * each question's own providerId.
 *
 * Concurrent calls with the same item set share one POST (in-flight dedup). The
 * cache merge re-reads the cache fresh immediately before writing so a refresh
 * fired before the await resolved can't clobber a concurrent write (lost update).
 *
 * @param unansweredDynamicQuestions — array of { questionId, providerId } pairs
 */
export const fetchDynamicQuestionsMultiProvider = async (
  unansweredDynamicQuestions: Array<{ questionId: string; providerId: string }>
): Promise<void> => {
  if (!unansweredDynamicQuestions.length) {
    return;
  }

  const inFlightKey = buildInFlightKey(unansweredDynamicQuestions);

  let questionsPromise = inFlightQuestionFetches.get(inFlightKey);
  if (!questionsPromise) {
    questionsPromise = getDynamicScreeningQuestionsForProviders({
      questions: unansweredDynamicQuestions
    })
      .then((response) => response?.data?.questions ?? [])
      .catch((error) => {
        logError('Error fetching multi-provider dynamic screener questions:', error);
        return [];
      })
      .finally(() => {
        inFlightQuestionFetches.delete(inFlightKey);
      });
    inFlightQuestionFetches.set(inFlightKey, questionsPromise);
  }

  const questions = await questionsPromise;

  if (!questions.length) {
    return;
  }

  // Re-read the cache fresh right before the synchronous merge so this write can't
  // overwrite changes made by a concurrent fetch that resolved during our await.
  const cache = getDynamicScreenerCache();

  // Group returned questions into the cache keyed by each question's own
  // providerId, replacing any existing entry with the same id so repeated
  // fetches refresh in place rather than accumulating duplicates.
  questions.forEach((q) => {
    const key = q.providerId ?? '';
    if (!key) {
      return;
    }
    const withoutDuplicate = (cache.questions[key] || []).filter((existing) => existing.id !== q.id);
    cache.questions[key] = [...withoutDuplicate, q];
  });

  setDynamicScreenerCache(cache);
};

interface GroupedUnansweredDynamicQuestions {
  [providerId: string]: string[];
}

/**
 * Group a survey list's unanswered dynamic questions by their owning provider,
 * keeping only providers enabled by the split test. De-duplication is per
 * provider+question, so the same questionId under two providers is kept once each.
 * @param surveyList the survey list
 * @param allowedProviders the split-test enabled provider IDs
 * @returns questionIds grouped by owning providerId (string key)
 */
export const groupUnansweredDynamicQuestions = (surveyList: UspSurveysModel[], allowedProviders: number[]): GroupedUnansweredDynamicQuestions => {
  const grouped: { [providerId: string]: Set<string> } = {};

  surveyList.forEach((survey) => {
    survey.unansweredDynamicQuestions?.forEach(({ questionId, providerId }) => {
      if (!allowedProviders.includes(Number(providerId))) {
        return;
      }
      grouped[providerId] = grouped[providerId] || new Set();
      grouped[providerId].add(questionId);
    });
  });

  return Object.fromEntries(
    Object.entries(grouped).map(([providerId, questions]) => [providerId, Array.from(questions)])
  );
};

/**
 * Process the dynamic screener questions for all surveys: gather every survey's
 * unanswered {questionId, providerId} pairs (filtered to the split-test's enabled
 * providers, de-duplicated per provider+question) and fetch them for all providers
 * in a single multi-provider POST.
 * @param surveyList the survey list
 */
export const processDynamicQuestions = async (surveyList: UspSurveysModel[]): Promise<void> => {
  const splitTest = getDynamicScreenerTestData();
  if (!splitTest.enabled || !splitTest.providerIds.length) {
    return;
  }

  const cache   = getDynamicScreenerCache();
  const grouped = groupUnansweredDynamicQuestions(surveyList, splitTest.providerIds);

  // Only fetch questions we don't already have cached for that provider, so a
  // survey-list refresh doesn't re-POST (and re-cache) questions already loaded.
  const items = Object.entries(grouped).flatMap(([providerId, questionIds]) => {
    const cachedQuestions = cache.questions[providerId] || [];
    return questionIds
      .filter((questionId) => !cachedQuestions.some((q) => q.id === questionId))
      .map((questionId) => ({ questionId, providerId }));
  });

  if (!items.length) {
    return;
  }

  await fetchDynamicQuestionsMultiProvider(items);
};

/**
 * Get the answer type for a dynamic screener question
 * @param question
 * @returns
 */
const getAnswerType = (question: DynamicScreenerQuestion): USPAnswerType => {
  switch (question.displayMode.inputType) {
    case 'TEXT':
      return USPAnswerType.TEXT;
    case 'NUMBER':
      return USPAnswerType.NUMBER;
    default:
      return USPAnswerType.TEXT;
  }
};

/**
 * Maps dynamic screener questions to USP format.
 *
 * The function first filters questions based on three criteria:
 * - Questions must have a valid displayMode.type (SINGLE_CHOICE, MULTI_CHOICE, or OPEN_ENDED)
 * - Single and multi choice questions must have answer options
 * - Open-ended questions are kept regardless of answers
 *
 * Then maps the filtered questions to the USP format with the following:
 * - Converts question IDs to numbers
 * - Sets appropriate answer types based on question type
 * - Handles multi-value questions for multiple choice
 * - Maps predefined answers when available
 * - Includes constraints for open-ended questions
 * - Uses categoryId to store providerId for answer submission
 *
 * @param questions - Array of dynamic screener questions to process (each carries its own providerId)
 * @returns Array of questions in USP format
 */
export const mapDynamicScreenerQuestions = (questions: DynamicScreenerQuestion[]): UspUnansweredQuestions[] => {
  return questions
    .filter(q => {
      // Drop questions with blank id
      if (!q.id || q.id.trim() === '') {
        return false;
      }

      // Drop questions with blank question text
      if (!q.question || q.question.trim() === '') {
        return false;
      }

      // Drop questions with a blank or absent owning providerId
      if (!q.providerId || q.providerId.trim() === '') {
        return false;
      }

      const displayType = q.displayMode?.type?.toUpperCase();
      // Filter out questions with an invalid or missing displayMode.type
      if (displayType !== 'SINGLE_CHOICE' && displayType !== 'MULTI_CHOICE' && displayType !== 'OPEN_ENDED') {
        return false;
      }

      // For single and multi choice, must have valid answer options
      if (displayType === 'SINGLE_CHOICE' || displayType === 'MULTI_CHOICE') {
        const validAnswers = q.answers?.filter(a => a.id && a.id.trim() !== '' && a.label && a.label.trim() !== '') ?? [];
        return validAnswers.length > 0;
      }

      // Keep open-ended questions
      return displayType === 'OPEN_ENDED';
    })
    .map(question => {
      const displayType = question.displayMode.type?.toUpperCase();

      // Filter valid answer options (non-blank id and label)
      const validAnswers = question.answers?.filter(
        a => a.id && a.id.trim() !== '' && a.label && a.label.trim() !== ''
      ) ?? [];

      let constraints = question.displayMode.constraints;
      if (
        constraints?.constraintType === 'NUMBER' &&
        (constraints.min === undefined || constraints.max === undefined)
      ) {
        constraints = undefined;
      }

      const effectiveProviderId = Number(question.providerId);

      return {
        id                  : Number(question.id),
        inputTypeId         : 1,
        key                 : buildDynamicScreenerKey(effectiveProviderId, question.id),
        question            : question.question,
        answerType          : displayType !== 'OPEN_ENDED' ? USPAnswerType.TEXT : getAnswerType(question),
        multiValue          : displayType === 'MULTI_CHOICE',
        hasPredefinedAnswers: displayType !== 'OPEN_ENDED',
        enabled             : true,
        sortOrder           : 100,
        predefinedAnswers   : validAnswers.map((answer, index) => ({
          id         : Number(answer.id),
          questionId : Number(question.id),
          label      : answer.label,
          sortOrder  : index + 1,
          enabled    : true,
          answerValue: answer.id,
          solo       : false
        })),
        categoryId  : effectiveProviderId,
        required    : true,
        typeId      : 0,
        categoryName: '',
        criteriaKey : undefined,
        constraints
      };
    });
};

/**
 * Maps a survey's unanswered dynamic-screener questions to the UPM unanswered-questions
 * format, looking each one up in the cache by its own owning provider so cross-provider
 * questions stay attributed correctly. Fires the dynamic-screener start analytics event
 * when at least one question resolves.
 * @param survey
 * @returns
 */
export const mapDynamicScreenerQuestionsToUnansweredQuestions = (survey: UspSurveysModel): UspUnansweredQuestions[] => {

  if (!survey.unansweredDynamicQuestions || survey.unansweredDynamicQuestions.length === 0) {
    return [];
  }

  const cacheData = getUserCacheData(UserSessionCacheKeys.DYNAMIC_SCREENER_QUESTIONS);

  const matched = survey.unansweredDynamicQuestions
    .map(({ questionId, providerId }) => {
      const providerCache: DynamicScreenerQuestion[] = cacheData?.questions?.[providerId] || [];
      return providerCache.find(q => q.id === questionId);
    })
    .filter((q): q is DynamicScreenerQuestion => Boolean(q));

  const result = mapDynamicScreenerQuestions(matched);

  if (result.length === 0) {
    return [];
  }

  analyticsTrackDynamicScreenerStart(survey.id, survey.providerId.toString(), result.length);
  return result;
};
